/**
 * Refresh Button Component
 * 
 * Reusable refresh button with loading animation.
 * Can be used across all pages in hub applications.
 */

import React from 'react';
import { RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

export interface RefreshButtonProps {
  onClick: () => void;
  isLoading?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'ghost';
  className?: string;
  tooltip?: string;
}

const sizeClasses = {
  sm: 'p-1.5',
  md: 'p-2.5',
  lg: 'p-3',
};

const iconSizes = {
  sm: 14,
  md: 16,
  lg: 18,
};

export const RefreshButton: React.FC<RefreshButtonProps> = ({
  onClick,
  isLoading = false,
  size = 'md',
  variant = 'default',
  className = '',
  tooltip = 'Refresh',
}) => {
  const baseClasses = variant === 'default'
    ? 'bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
    : 'bg-transparent hover:bg-gray-100';

  return (
    <motion.button
      onClick={onClick}
      disabled={isLoading}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      title={tooltip}
      className={`
        ${sizeClasses[size]}
        ${baseClasses}
        rounded-xl
        text-gray-700
        transition-all
        disabled:opacity-50
        disabled:cursor-not-allowed
        ${className}
      `}
    >
      <RefreshCw
        size={iconSizes[size]}
        className={isLoading ? 'animate-spin' : ''}
      />
    </motion.button>
  );
};

export default RefreshButton;


